import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { stripUndefined } from '@modules/content-plan/utils/stripUndefined';
import type {
  PlatformDesignProfile,
  PlatformDesignProfileSet,
  PlatformDesignProfileScope,
  PostTemplate,
} from '@segmently/schema-models/content-plan';

/**
 * Asset slot descriptor inside SystemPostFormat.assetSlots (free-form JSON in
 * the admin editor). Two explicit sizing properties (v2.20.0):
 * - aspectRatio: ratio hint, e.g. "1:1" / "16:9" (not selectable as a
 *   container preset by itself);
 * - pixelSize: full pixel dimensions — slots WITH pixelSize appear as named
 *   container presets in the article screen editor (presetRef
 *   "platformId/formatId[/slotKey]").
 */
export interface SystemAssetSlot {
  slotKey?: string;
  name?: string;
  aspectRatio?: string;
  pixelSize?: { width: number; height: number };
  [key: string]: unknown;
}

export interface SystemPostFormat {
  id: string;
  name?: string;
  description?: string;
  formatType?: string;
  constraints?: string[];
  assetSlots?: SystemAssetSlot[];
  hookInstructions?: string;
  formatSpecificInstructions?: string;
  isActive?: boolean;
  [key: string]: unknown;
}

export interface SystemPlatform {
  id: string;
  platformId: string;
  displayName: string;
  icon?: string;
  maxPostLength?: number;
  optimalPostFrequency?: { min?: number; max?: number };
  optimalTimes?: Array<string | { day?: string | number; hour?: number; score?: number }>;
  optimalSlots?: Array<{ day?: string | number; timeLocal?: string; score?: number }>;
  linkPenalty?: string | number;
  linkPlacementRule?: string;
  contentLifespanHours?: number;
  promptRef?: string;
  platformSpecificInstructions?: string;
  defaultHashtagPolicy?: string;
  defaultEmojiPolicy?: string;
  rewardSignals?: string[];
  forbiddenPatterns?: string[];
  rewardWeights?: Array<{ signal?: string; weight?: string; notes?: string }>;
  lengthPresets?: Array<{ name?: string; range?: string; notes?: string }>;
  assetGuidelines?: Array<Record<string, unknown>>;
  hookTemplates?: Record<string, string[]>;
  structureGuidelines?: string[];
  realWorldAnchors?: Array<{ name?: string; lesson?: string }>;
  voiceCalibration?: string;
  platformNotes?: string[];
  defaultFormatIds?: string[];
  isActive?: boolean;
  [key: string]: unknown;
}

export interface SystemPlatformWithFormats extends SystemPlatform {
  formats: SystemPostFormat[];
  postTemplates?: PostTemplate[];
  brandStyleKits?: SystemBrandStyleKitSet[];
  designSystemPackages?: SystemDesignSystemPackage[];
  catalogWarnings?: string[];
}

export interface SystemBrandStyleKitSet extends Omit<PlatformDesignProfileSet, 'profileKey'> {
  profileKey: PlatformDesignProfileScope | string;
  profiles?: SystemBrandStyleKitProfile[];
  [key: string]: unknown;
}

export interface SystemBrandStyleKitProfile extends PlatformDesignProfile {
  id: string;
  [key: string]: unknown;
}

export type DesignSystemPackageStatus = 'draft' | 'reviewed' | 'archived';

export type SystemDesignSystemPackageProfile = Omit<SystemBrandStyleKitProfile, 'profileKey'> & {
  packageId: string;
  profileKey: PlatformDesignProfileScope | string;
  contentHash?: string | null;
};

export type SystemDesignSystemPackageProfileInput =
  Partial<SystemDesignSystemPackageProfile>
  & {
    profileKey: PlatformDesignProfileScope | string;
    id?: string;
    packageId?: string;
    contentHash?: string | null;
  };

export interface SystemDesignSystemPackage {
  id: string;
  packageId: string;
  name?: string;
  description?: string;
  status?: DesignSystemPackageStatus;
  targetProfileKeys?: string[];
  completeProfileKeys?: string[];
  profileCount?: number;
  visualFoundation?: Record<string, unknown>;
  contentHash?: string | null;
  current?: boolean;
  profiles?: SystemDesignSystemPackageProfile[];
  [key: string]: unknown;
}

export interface SystemDesignSystemPackageSaveInput {
  id?: string;
  packageId?: string;
  name?: string;
  description?: string;
  status?: DesignSystemPackageStatus;
  targetProfileKeys?: string[];
  visualFoundation?: Record<string, unknown>;
  contentHash?: string | null;
  profiles: SystemDesignSystemPackageProfileInput[];
  [key: string]: unknown;
}

export interface SystemPlatformDraft {
  id?: string;
  platformId: string;
  displayName: string;
  icon?: string;
  maxPostLength?: number;
  optimalPostFrequency?: { min?: number; max?: number };
  optimalTimes?: Array<string | { day?: string | number; hour?: number; score?: number }>;
  optimalSlots?: Array<{ day?: string | number; timeLocal?: string; score?: number }>;
  linkPenalty?: string | number;
  linkPlacementRule?: string;
  contentLifespanHours?: number;
  promptRef?: string;
  platformSpecificInstructions?: string;
  defaultHashtagPolicy?: string;
  defaultEmojiPolicy?: string;
  rewardSignals?: string[];
  forbiddenPatterns?: string[];
  rewardWeights?: Array<{ signal?: string; weight?: string; notes?: string }>;
  lengthPresets?: Array<{ name?: string; range?: string; notes?: string }>;
  assetGuidelines?: Array<Record<string, unknown>>;
  hookTemplates?: Record<string, string[]>;
  structureGuidelines?: string[];
  realWorldAnchors?: Array<{ name?: string; lesson?: string }>;
  voiceCalibration?: string;
  platformNotes?: string[];
  defaultFormatIds?: string[];
  isActive?: boolean;
  [key: string]: unknown;
}

export function validateSystemPlatformDraft(
  platform: SystemPlatformDraft,
  formats: SystemPostFormat[],
): string[] {
  const errors: string[] = [];
  const platformId = String(platform.platformId ?? '').trim();
  if (!platformId) errors.push('platformId is required');
  if (platformId && !/^[a-z0-9][a-z0-9_-]*$/.test(platformId)) {
    errors.push('platformId must use lowercase letters, numbers, underscores, or hyphens');
  }
  if (!String(platform.displayName ?? '').trim()) errors.push('displayName is required');
  if (platform.isActive !== false && formats.length === 0) {
    errors.push('Active system platforms require at least one nested post format');
  }
  if (platform.optimalSlots !== undefined && !Array.isArray(platform.optimalSlots)) {
    errors.push('optimalSlots must be an array');
  }

  const seen = new Set<string>();
  for (const format of formats) {
    const id = String(format.id ?? '').trim();
    if (!id) {
      errors.push('Every post format requires an id');
      continue;
    }
    if (!/^[a-z0-9][a-z0-9_-]*$/.test(id)) {
      errors.push(`Format id "${id}" must use lowercase letters, numbers, underscores, or hyphens`);
    }
    if (seen.has(id)) errors.push(`Duplicate format id "${id}"`);
    seen.add(id);
  }
  return errors;
}

function unwrapRequiredCatalogResult<T>(result: PromiseSettledResult<T>, label: string): T {
  if (result.status === 'fulfilled') return result.value;
  throw new Error(`Failed to load system platform ${label}: ${formatCatalogError(result.reason)}`);
}

function unwrapOptionalCatalogResult<T>(
  result: PromiseSettledResult<T>,
  label: string,
  platformId: string,
  fallback: T,
): T {
  if (result.status === 'fulfilled') return result.value;
  // Keep older platform catalogue sections usable while newly introduced nested
  // collections wait for Firestore rules to be deployed in an environment.
  console.warn(
    `[SystemPlatformService] ${label} unavailable for ${platformId}: ${formatCatalogError(result.reason)}`,
  );
  return fallback;
}

function optionalCatalogWarning(result: PromiseSettledResult<unknown>, label: string): string | null {
  if (result.status === 'fulfilled') return null;
  return `Could not load ${label}: ${formatCatalogError(result.reason)}`;
}

function formatCatalogError(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'unknown error');
}

const DESIGN_PROFILE_ORDER = [
  'design',
  'single_image_landscape',
  'single_image_square',
  'single_image_portrait',
  'carousel_square',
  'carousel_portrait',
];

function profileKeySortIndex(profileKey: string): number {
  const index = DESIGN_PROFILE_ORDER.indexOf(profileKey);
  return index >= 0 ? index : DESIGN_PROFILE_ORDER.length + 1;
}

function targetProfileLabel(profileKey: string): string {
  return profileKey
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function normalizeDesignSystemPackageId(value: unknown, label: string): string {
  const normalized = String(value ?? '').trim();
  if (!normalized) throw new Error(`${label} is required`);
  if (!/^[a-z0-9][a-z0-9_-]*$/.test(normalized)) {
    throw new Error(`${label} must use lowercase letters, numbers, underscores, or hyphens`);
  }
  return normalized;
}

function normalizeDesignSystemPackageProfiles(
  profiles: SystemDesignSystemPackageProfileInput[],
  packageId: string,
  platformId: string,
): SystemDesignSystemPackageProfile[] {
  return profiles.map((profile) => {
    const profileKey = normalizeDesignSystemPackageId(profile.profileKey, 'profileKey');
    return {
      ...profile,
      id: profileKey,
      packageId,
      platformId,
      profileKey,
      profileId: String(profile.profileId ?? packageId),
      source: String(profile.source ?? 'package_profile') as any,
    } as SystemDesignSystemPackageProfile;
  });
}

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    const normalized = String(value ?? '').trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    result.push(normalized);
  });
  return result;
}

function systemDesignProfilePayload(
  profile: SystemBrandStyleKitProfile | SystemDesignSystemPackageProfile,
  opts: {
    platformId: string;
    profileKey: string;
    profileId: string;
    source?: string;
  },
): PlatformDesignProfile & Record<string, unknown> {
  const payload = { ...(profile as Record<string, unknown>) };
  delete payload.id;
  delete payload.createdAt;
  delete payload.updatedAt;
  return {
    ...payload,
    platformId: opts.platformId,
    profileId: opts.profileId,
    profileKey: opts.profileKey,
    source: opts.source ?? (profile as any).source ?? 'manual',
    isComplete: (profile as any).isComplete ?? true,
  } as PlatformDesignProfile & Record<string, unknown>;
}

class SystemPlatformService {
  private get db() {
    return CoreFirebaseService.getInstance().firestore;
  }

  private platformCollectionRef() {
    return collection(this.db, 'system_platforms');
  }

  private platformDocRef(platformId: string) {
    return doc(this.db, 'system_platforms', platformId);
  }

  private formatCollectionRef(platformId: string) {
    return collection(this.db, 'system_platforms', platformId, 'formats');
  }

  private formatDocRef(platformId: string, formatId: string) {
    return doc(this.db, 'system_platforms', platformId, 'formats', formatId);
  }

  private templateCollectionRef(platformId: string) {
    return collection(this.db, 'system_platforms', platformId, 'post_templates');
  }

  private templateDocRef(platformId: string, templateId: string) {
    return doc(this.db, 'system_platforms', platformId, 'post_templates', templateId);
  }

  private brandStyleKitCollectionRef(platformId: string) {
    return collection(this.db, 'system_platforms', platformId, 'brand_style_kit');
  }

  private brandStyleKitDocRef(platformId: string, profileKey: string) {
    return doc(this.db, 'system_platforms', platformId, 'brand_style_kit', profileKey);
  }

  private brandStyleKitProfilesCollectionRef(platformId: string, profileKey: string) {
    return collection(this.db, 'system_platforms', platformId, 'brand_style_kit', profileKey, 'profiles');
  }

  private brandStyleKitProfileDocRef(platformId: string, profileKey: string, profileId: string) {
    return doc(this.db, 'system_platforms', platformId, 'brand_style_kit', profileKey, 'profiles', profileId);
  }

  private designSystemPackageCollectionRef(platformId: string) {
    return collection(this.db, 'system_platforms', platformId, 'design_system_packages');
  }

  private designSystemPackageDocRef(platformId: string, packageId: string) {
    return doc(this.db, 'system_platforms', platformId, 'design_system_packages', packageId);
  }

  private designSystemPackageProfileCollectionRef(platformId: string, packageId: string) {
    return collection(this.designSystemPackageDocRef(platformId, packageId), 'profiles');
  }

  private designSystemPackageProfileDocRef(platformId: string, packageId: string, profileKey: string) {
    return doc(this.designSystemPackageDocRef(platformId, packageId), 'profiles', profileKey);
  }

  async listPlatforms(opts?: { activeOnly?: boolean }): Promise<SystemPlatform[]> {
    const snap = await getDocs(this.platformCollectionRef());
    return snap.docs
      .map((d) => this.fromPlatformDoc(d.id, d.data()))
      .filter((platform) => opts?.activeOnly !== true || platform.isActive !== false)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  subscribePlatforms(
    callback: (platforms: SystemPlatform[]) => void,
    onError?: (error: Error) => void,
    opts?: { activeOnly?: boolean },
  ): Unsubscribe {
    return onSnapshot(
      query(this.platformCollectionRef()),
      (snap) => {
        const platforms = snap.docs
          .map((d) => this.fromPlatformDoc(d.id, d.data()))
          .filter((platform) => opts?.activeOnly !== true || platform.isActive !== false)
          .sort((a, b) => a.displayName.localeCompare(b.displayName));
        callback(platforms);
      },
      (err) => onError?.(err as Error),
    );
  }

  async getPlatform(platformId: string): Promise<SystemPlatformWithFormats | null> {
    const snap = await getDoc(this.platformDocRef(platformId));
    if (!snap.exists()) return null;
    const [
      formatsResult,
      postTemplatesResult,
      brandStyleKitsResult,
      designSystemPackagesResult,
    ] = await Promise.allSettled([
      this.listFormats(platformId),
      this.listPostTemplates(platformId),
      this.listBrandStyleKitSets(platformId, { includeProfiles: true }),
      this.listDesignSystemPackages(platformId, { includeProfiles: true }),
    ]);
    const formats = unwrapRequiredCatalogResult(formatsResult, 'formats');
    const postTemplates = unwrapOptionalCatalogResult(postTemplatesResult, 'post templates', platformId, []);
    const brandStyleKits = unwrapOptionalCatalogResult(brandStyleKitsResult, 'brand style kits', platformId, []);
    const designSystemPackages = unwrapOptionalCatalogResult(
      designSystemPackagesResult,
      'design system packages',
      platformId,
      [],
    );
    const catalogWarnings = [
      optionalCatalogWarning(postTemplatesResult, 'post templates'),
      optionalCatalogWarning(brandStyleKitsResult, 'brand style kits'),
      optionalCatalogWarning(designSystemPackagesResult, 'design system packages'),
    ].filter(Boolean) as string[];
    return {
      ...this.fromPlatformDoc(snap.id, snap.data()),
      formats,
      postTemplates,
      brandStyleKits,
      designSystemPackages,
      catalogWarnings,
    };
  }

  async listFormats(platformId: string): Promise<SystemPostFormat[]> {
    const snap = await getDocs(this.formatCollectionRef(platformId));
    return snap.docs
      .map((d) => ({ ...(d.data() as SystemPostFormat), id: d.id }))
      .sort((a, b) => String(a.name ?? a.id).localeCompare(String(b.name ?? b.id)));
  }

  async listPostTemplates(platformId: string): Promise<PostTemplate[]> {
    const snap = await getDocs(this.templateCollectionRef(platformId));
    return snap.docs
      .map((d) => ({ ...(d.data() as PostTemplate), id: d.id }))
      .sort((a, b) => String(a.name ?? a.id).localeCompare(String(b.name ?? b.id)));
  }

  async listBrandStyleKitSets(platformId: string, opts?: { includeProfiles?: boolean }): Promise<SystemBrandStyleKitSet[]> {
    const snap = await getDocs(this.brandStyleKitCollectionRef(platformId));
    const sets = snap.docs
      .map((d) => ({ ...(d.data() as PlatformDesignProfileSet), profileKey: d.id } as SystemBrandStyleKitSet))
      .filter((set) => set.isActive !== false)
      .sort((a, b) => String(a.displayName ?? a.profileKey).localeCompare(String(b.displayName ?? b.profileKey)));
    if (opts?.includeProfiles !== true) return sets;
    return Promise.all(
      sets.map(async (set) => ({
        ...set,
        profiles: await this.listBrandStyleKitProfiles(platformId, set.profileKey),
      })),
    );
  }

  async listBrandStyleKitProfiles(platformId: string, profileKey: string): Promise<SystemBrandStyleKitProfile[]> {
    const snap = await getDocs(this.brandStyleKitProfilesCollectionRef(platformId, profileKey));
    return snap.docs
      .map((d) => ({ ...(d.data() as PlatformDesignProfile), id: d.id } as SystemBrandStyleKitProfile))
      .filter((profile) => profile.isComplete !== false)
      .sort((a, b) => String(a.name ?? a.id).localeCompare(String(b.name ?? b.id)));
  }

  async getCurrentBrandStyleKitProfile(platformId: string, profileKey: string): Promise<SystemBrandStyleKitProfile | null> {
    const setSnap = await getDoc(this.brandStyleKitDocRef(platformId, profileKey));
    if (!setSnap.exists()) return null;
    const currentProfileId = String(setSnap.data().currentProfileId ?? '').trim();
    if (currentProfileId) {
      const profileSnap = await getDoc(this.brandStyleKitProfileDocRef(platformId, profileKey, currentProfileId));
      if (profileSnap.exists()) {
        return { ...(profileSnap.data() as PlatformDesignProfile), id: profileSnap.id } as SystemBrandStyleKitProfile;
      }
    }
    const profiles = await this.listBrandStyleKitProfiles(platformId, profileKey);
    return profiles[0] ?? null;
  }

  async listDesignSystemPackages(
    platformId: string,
    opts?: { includeProfiles?: boolean },
  ): Promise<SystemDesignSystemPackage[]> {
    const [platformSnap, packagesSnap] = await Promise.all([
      getDoc(this.platformDocRef(platformId)),
      getDocs(this.designSystemPackageCollectionRef(platformId)),
    ]);
    const currentPackageId = String(platformSnap.data()?.currentDesignSystemPackageId ?? '').trim();
    const packages = packagesSnap.docs
      .map((d) => {
        const data = d.data() as SystemDesignSystemPackage;
        return {
          ...data,
          id: d.id,
          packageId: String(data.packageId ?? d.id),
          current: currentPackageId ? d.id === currentPackageId : data.current === true,
        } as SystemDesignSystemPackage;
      })
      .filter((item) => item.status !== 'archived')
      .sort((a, b) => {
        if (a.current !== b.current) return a.current ? -1 : 1;
        return String(a.name ?? a.packageId).localeCompare(String(b.name ?? b.packageId));
      });
    if (opts?.includeProfiles !== true) return packages;
    return Promise.all(
      packages.map(async (item) => ({
        ...item,
        profiles: await this.listDesignSystemPackageProfiles(platformId, item.packageId),
      })),
    );
  }

  async listDesignSystemPackageProfiles(
    platformId: string,
    packageId: string,
  ): Promise<SystemDesignSystemPackageProfile[]> {
    const normalizedPackageId = normalizeDesignSystemPackageId(packageId, 'packageId');
    const snap = await getDocs(this.designSystemPackageProfileCollectionRef(platformId, normalizedPackageId));
    return snap.docs
      .map((d) => ({
        ...(d.data() as PlatformDesignProfile),
        id: d.id,
        packageId: normalizedPackageId,
        profileKey: String((d.data() as any).profileKey ?? d.id),
      } as SystemDesignSystemPackageProfile))
      .sort((a, b) => profileKeySortIndex(String(a.profileKey)) - profileKeySortIndex(String(b.profileKey)));
  }

  async saveDesignSystemPackage(opts: {
    platformId: string;
    designPackage: SystemDesignSystemPackageSaveInput;
    setCurrent?: boolean;
  }): Promise<void> {
    const platformId = String(opts.platformId ?? '').trim();
    if (!platformId) throw new Error('platformId is required');
    const packageId = normalizeDesignSystemPackageId(
      opts.designPackage.packageId ?? opts.designPackage.id,
      'packageId',
    );
    const profiles = normalizeDesignSystemPackageProfiles(opts.designPackage.profiles, packageId, platformId);
    if (profiles.length === 0) {
      throw new Error('At least one design system package profile is required.');
    }
    const targetProfileKeys = uniqueStrings([
      ...(opts.designPackage.targetProfileKeys ?? []),
      ...profiles.map((profile) => String(profile.profileKey)),
    ]);
    const completeProfileKeys = uniqueStrings(
      profiles
        .filter((profile) => profile.isComplete !== false)
        .map((profile) => String(profile.profileKey)),
    );
    const now = serverTimestamp() as unknown;
    const batch = writeBatch(this.db);
    batch.set(
      this.designSystemPackageDocRef(platformId, packageId),
      stripUndefined({
        ...opts.designPackage,
        id: undefined,
        profiles: undefined,
        packageId,
        name: opts.designPackage.name ?? packageId,
        status: opts.designPackage.status ?? 'draft',
        targetProfileKeys,
        completeProfileKeys,
        profileCount: profiles.length,
        current: opts.setCurrent === true ? true : opts.designPackage.current,
        updatedAt: now,
      }) as Record<string, unknown>,
      { merge: true },
    );
    profiles.forEach((profile) => {
      const profileKey = String(profile.profileKey);
      batch.set(
        this.designSystemPackageProfileDocRef(platformId, packageId, profileKey),
        stripUndefined({
          ...systemDesignProfilePayload(profile, {
            platformId,
            profileKey,
            profileId: String(profile.profileId ?? packageId),
            source: 'package_profile',
          }),
          packageId,
          contentHash: profile.contentHash,
          updatedAt: now,
        }) as Record<string, unknown>,
        { merge: true },
      );
      if (opts.setCurrent === true) {
        this.materializeDesignSystemPackageProfile(batch, {
          platformId,
          packageId,
          packageName: opts.designPackage.name ?? packageId,
          packageContentHash: opts.designPackage.contentHash,
          profile,
          now,
        });
      }
    });
    if (opts.setCurrent === true) {
      batch.set(
        this.platformDocRef(platformId),
        stripUndefined({
          currentDesignSystemPackageId: packageId,
          updatedAt: now,
        }) as Record<string, unknown>,
        { merge: true },
      );
    }
    await batch.commit();
  }

  async makeDesignSystemPackageCurrent(platformId: string, packageId: string): Promise<void> {
    const normalizedPackageId = normalizeDesignSystemPackageId(packageId, 'packageId');
    const packageSnap = await getDoc(this.designSystemPackageDocRef(platformId, normalizedPackageId));
    if (!packageSnap.exists()) {
      throw new Error(`Design system package "${normalizedPackageId}" does not exist.`);
    }
    const profiles = await this.listDesignSystemPackageProfiles(platformId, normalizedPackageId);
    if (profiles.length === 0) {
      throw new Error(`Design system package "${normalizedPackageId}" has no profiles to materialize.`);
    }
    const packageData = packageSnap.data() as SystemDesignSystemPackage;
    const now = serverTimestamp() as unknown;
    const batch = writeBatch(this.db);
    profiles.forEach((profile) => {
      this.materializeDesignSystemPackageProfile(batch, {
        platformId,
        packageId: normalizedPackageId,
        packageName: packageData.name ?? normalizedPackageId,
        packageContentHash: packageData.contentHash,
        profile,
        now,
      });
    });
    batch.set(
      this.designSystemPackageDocRef(platformId, normalizedPackageId),
      stripUndefined({
        current: true,
        updatedAt: now,
      }) as Record<string, unknown>,
      { merge: true },
    );
    batch.set(
      this.platformDocRef(platformId),
      stripUndefined({
        currentDesignSystemPackageId: normalizedPackageId,
        updatedAt: now,
      }) as Record<string, unknown>,
      { merge: true },
    );
    await batch.commit();
  }

  private materializeDesignSystemPackageProfile(
    batch: ReturnType<typeof writeBatch>,
    opts: {
      platformId: string;
      packageId: string;
      packageName?: string;
      packageContentHash?: string | null;
      profile: SystemDesignSystemPackageProfile;
      now: unknown;
    },
  ): void {
    const profileKey = String(opts.profile.profileKey);
    const payload = stripUndefined({
      ...systemDesignProfilePayload(opts.profile, {
        platformId: opts.platformId,
        profileKey,
        profileId: opts.packageId,
        source: 'package_projection',
      }),
      designPackageId: opts.packageId,
      designPackageName: opts.packageName ?? opts.packageId,
      designPackageContentHash: opts.packageContentHash,
      designPackageProfileContentHash: opts.profile.contentHash,
      updatedAt: opts.now,
    }) as Record<string, unknown>;
    batch.set(
      this.brandStyleKitDocRef(opts.platformId, profileKey),
      stripUndefined({
        profileKey,
        displayName: targetProfileLabel(profileKey),
        description: opts.profile.description ?? opts.profile.designDescription,
        currentProfileId: opts.packageId,
        currentDesignSystemPackageId: opts.packageId,
        isActive: true,
        updatedAt: opts.now,
      }) as Record<string, unknown>,
      { merge: true },
    );
    batch.set(
      this.brandStyleKitProfileDocRef(opts.platformId, profileKey, opts.packageId),
      payload,
      { merge: true },
    );
  }

  async saveBrandStyleKitProfile(opts: {
    platformId: string;
    profileKey: string;
    profile: SystemBrandStyleKitProfile;
    previousProfileKey?: string | null;
    previousProfileId?: string | null;
    setCurrent?: boolean;
  }): Promise<void> {
    const platformId = String(opts.platformId ?? '').trim();
    const profileKey = String(opts.profileKey ?? '').trim();
    const profileId = String(opts.profile.id ?? opts.profile.profileId ?? '').trim();
    if (!platformId) throw new Error('platformId is required');
    if (!/^[a-z0-9][a-z0-9_-]*$/.test(profileKey)) {
      throw new Error('profile key must use lowercase letters, numbers, underscores, or hyphens');
    }
    if (!/^[a-z0-9][a-z0-9_-]*$/.test(profileId)) {
      throw new Error('profile id must use lowercase letters, numbers, underscores, or hyphens');
    }

    const now = serverTimestamp() as unknown;
    const setRef = this.brandStyleKitDocRef(platformId, profileKey);
    const setSnap = await getDoc(setRef);
    const batch = writeBatch(this.db);
    batch.set(
      setRef,
      stripUndefined({
        profileKey,
        displayName: opts.profile.name ?? profileKey,
        description: opts.profile.description,
        currentProfileId: opts.setCurrent === false
          ? (setSnap.exists() ? setSnap.data().currentProfileId : profileId)
          : profileId,
        isActive: true,
        createdAt: setSnap.exists() ? setSnap.data().createdAt : now,
        updatedAt: now,
      }) as Record<string, unknown>,
      { merge: true },
    );

    const payload = { ...(opts.profile as Record<string, unknown>) };
    delete payload.id;
    delete payload.createdAt;
    delete payload.updatedAt;
    batch.set(
      this.brandStyleKitProfileDocRef(platformId, profileKey, profileId),
      stripUndefined({
        ...payload,
        profileId,
        profileKey,
        platformId,
        source: payload.source ?? 'manual',
        isComplete: payload.isComplete ?? true,
        createdAt: now,
        updatedAt: now,
      }) as Record<string, unknown>,
      { merge: true },
    );

    const oldKey = String(opts.previousProfileKey ?? '').trim();
    const oldId = String(opts.previousProfileId ?? '').trim();
    if (oldKey && oldId && (oldKey !== profileKey || oldId !== profileId)) {
      batch.delete(this.brandStyleKitProfileDocRef(platformId, oldKey, oldId));
    }

    await batch.commit();
  }

  async deleteBrandStyleKitProfile(platformId: string, profileKey: string, profileId: string): Promise<void> {
    const batch = writeBatch(this.db);
    batch.delete(this.brandStyleKitProfileDocRef(platformId, profileKey, profileId));
    await batch.commit();
  }

  async replacePlatformWithFormats(
    platform: SystemPlatformDraft,
    formats: SystemPostFormat[],
  ): Promise<void> {
    const platformId = String(platform.platformId ?? '').trim();
    const normalizedFormats = formats.map((format) => ({
      ...format,
      id: String(format.id ?? '').trim(),
    }));
    const errors = validateSystemPlatformDraft(
      { ...platform, platformId },
      normalizedFormats,
    );
    if (errors.length > 0) throw new Error(errors.join('; '));

    const platformPayload = { ...platform };
    delete platformPayload.id;
    const [existingPlatformSnap, existingFormats] = await Promise.all([
      getDoc(this.platformDocRef(platformId)),
      getDocs(this.formatCollectionRef(platformId)),
    ]);
    const nextFormatIds = new Set(normalizedFormats.map((format) => format.id));
    const existingFormatsById = new Map(existingFormats.docs.map((formatDoc) => [formatDoc.id, formatDoc.data()]));
    const now = serverTimestamp() as unknown;
    const batch = writeBatch(this.db);

    batch.set(
      this.platformDocRef(platformId),
      stripUndefined({
        ...platformPayload,
        platformId,
        displayName: String(platform.displayName ?? '').trim(),
        createdAt: existingPlatformSnap.exists() ? existingPlatformSnap.data().createdAt : now,
        updatedAt: now,
      }) as Record<string, unknown>,
    );

    for (const oldFormat of existingFormats.docs) {
      if (!nextFormatIds.has(oldFormat.id)) {
        batch.delete(this.formatDocRef(platformId, oldFormat.id));
      }
    }

    for (const format of normalizedFormats) {
      const { id, ...payload } = format;
      batch.set(
        this.formatDocRef(platformId, id),
        stripUndefined({
          ...payload,
          createdAt: existingFormatsById.get(id)?.createdAt ?? now,
          updatedAt: now,
        }) as Record<string, unknown>,
      );
    }

    await batch.commit();
  }

  async deletePlatform(platformId: string): Promise<void> {
    const [formats, templates, brandKitSets] = await Promise.all([
      getDocs(this.formatCollectionRef(platformId)),
      getDocs(this.templateCollectionRef(platformId)),
      getDocs(this.brandStyleKitCollectionRef(platformId)),
    ]);
    const batch = writeBatch(this.db);
    formats.docs.forEach((formatDoc) => batch.delete(this.formatDocRef(platformId, formatDoc.id)));
    templates.docs.forEach((templateDoc) => batch.delete(templateDoc.ref));
    for (const setDoc of brandKitSets.docs) {
      const profiles = await getDocs(this.brandStyleKitProfilesCollectionRef(platformId, setDoc.id));
      profiles.docs.forEach((profileDoc) => batch.delete(profileDoc.ref));
      batch.delete(setDoc.ref);
    }
    batch.delete(this.platformDocRef(platformId));
    await batch.commit();
  }

  async deleteFormat(platformId: string, formatId: string): Promise<void> {
    await deleteDoc(this.formatDocRef(platformId, formatId));
  }

  async savePostTemplate(
    platformId: string,
    template: PostTemplate & Record<string, unknown>,
    previousTemplateId?: string | null,
  ): Promise<void> {
    const normalizedPlatformId = String(platformId ?? '').trim();
    const templateId = String(template.id ?? '').trim();
    if (!normalizedPlatformId) throw new Error('platformId is required');
    if (!templateId) throw new Error('template id is required');
    if (!/^[a-z0-9][a-z0-9_-]*$/.test(templateId)) {
      throw new Error('template id must use lowercase letters, numbers, underscores, or hyphens');
    }

    const targetRef = this.templateDocRef(normalizedPlatformId, templateId);
    const targetSnap = await getDoc(targetRef);
    const now = serverTimestamp() as unknown;
    const payload = { ...(template as Record<string, unknown>) };
    delete payload.id;
    delete payload.createdAt;
    delete payload.updatedAt;
    const batch = writeBatch(this.db);

    batch.set(
      targetRef,
      stripUndefined({
        ...payload,
        id: templateId,
        scope: String((payload as any).scope ?? 'system'),
        createdAt: targetSnap.exists() ? targetSnap.data().createdAt : now,
        updatedAt: now,
      }) as Record<string, unknown>,
    );

    const oldId = String(previousTemplateId ?? '').trim();
    if (oldId && oldId !== templateId) {
      batch.delete(this.templateDocRef(normalizedPlatformId, oldId));
    }

    await batch.commit();
  }

  async deletePostTemplate(platformId: string, templateId: string): Promise<void> {
    await deleteDoc(this.templateDocRef(platformId, templateId));
  }

  private fromPlatformDoc(id: string, data: Record<string, unknown>): SystemPlatform {
    return {
      ...(data as Omit<SystemPlatform, 'id' | 'platformId' | 'displayName'>),
      id,
      platformId: String(data.platformId ?? id),
      displayName: String(data.displayName ?? data.platformId ?? id),
    };
  }
}

export const systemPlatformService = new SystemPlatformService();
export { SystemPlatformService };
